import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/20/solid';
import {
  BackgroundTaskStatus,
  ExportTaskEntity,
  IExportTaskItem,
} from 'shared';

import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';
import { useFetchExportTasksQuery, useLazyFetchExportDownloadUrlQuery } from '../../services/api/exportsApi/exports';
import { classNames } from '../../../common/lib/classNames';

const MAX_LISTED_EXPORTS = 10;

const POLL_INTERVAL_MS = 30_000;

function ExportJobRow({ job, organisationId }: { job: IExportTaskItem; organisationId: string }) {
  const [fetchDownloadUrl] = useLazyFetchExportDownloadUrlQuery();
  const [downloading, setDownloading] = useState(false);

  const pending = job.status === BackgroundTaskStatus.PENDING;
  const failed = job.status === BackgroundTaskStatus.FAILED;

  async function onDownload() {
    setDownloading(true);

    try {
      const { url } = await fetchDownloadUrl({ organisationId, id: job.id }).unwrap();

      window.open(url, `_blank`, `noopener noreferrer`);
    }
    catch {
      toast.error(`Could not download the export, it may have expired`);
    }
    finally {
      setDownloading(false);
    }
  }

  function renderStatus() {
    if (pending) {
      return <ArrowPathIcon className={ `h-4 w-4 shrink-0 animate-spin text-gray-400` } />;
    }

    if (failed) {
      return (
        <span
          title={ `Export failed` }
          className={ `shrink-0` }
        >
          <ExclamationCircleIcon className={ `h-4 w-4 text-red-400` } />
        </span>
      );
    }

    return (
      <button
        type={ `button` }
        onClick={ onDownload }
        disabled={ downloading }
        title={ `Download CSV` }
        className={ `shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700` }
      >
        <ArrowDownTrayIcon className={ classNames(`h-4 w-4`, downloading && `animate-pulse`) } />
      </button>
    );
  }

  return (
    <div className={ `flex items-center gap-1.5 rounded-md px-2 py-1.5` }>
      <div className={ `min-w-0 flex-1` }>
        <p className={ `truncate text-sm text-gray-600` }
          title={ job.filename }>
          { job.filename || `${job.entity[0].toUpperCase()}${job.entity.slice(1)} export` }
        </p>
        <p className={ `text-xs text-gray-400` }>
          { formatDistanceToNow(new Date(job.scheduledAt ?? job.createdAt), { addSuffix: true }) }
        </p>
      </div>
      { renderStatus() }
    </div>
  );
}

// Recent CSV export jobs for the invoice/contact table, shown in the nested
// sidebar under the quick filters. Complete jobs can be re-downloaded until
// the stored file expires (14 days).
export function ExportJobsNav({ entity }: { entity: ExportTaskEntity }) {
  const currentOrg = useGetSelectedOrganisation();

  const { data } = useFetchExportTasksQuery(
    { organisationId: currentOrg?.id ?? `` },
    { skip: !currentOrg?.id, pollingInterval: POLL_INTERVAL_MS, skipPollingIfUnfocused: true },
  );

  // Hide expired jobs (complete but the stored file is gone/expired) and scheduled exports that
  // haven't run yet (those live under Settings > Advanced > Scheduled Exports)
  const jobs = (data?.exports ?? [])
    .filter(job => job.entity === entity)
    .filter(job => job.status !== BackgroundTaskStatus.COMPLETE || job.downloadable)
    .filter(job => job.status !== BackgroundTaskStatus.PENDING || !job.savedFilterId)
    .slice(0, MAX_LISTED_EXPORTS);

  if (!jobs.length || !currentOrg?.id) return null;

  return (
    <div className={ `mt-6 px-2` }>
      <p className={ `px-2 text-xs font-semibold uppercase tracking-wider text-gray-400` }>
        { `Exports` }
      </p>
      <div className={ `mt-1 space-y-1` }>
        { jobs.map(job => (
          <ExportJobRow
            key={ job.id }
            job={ job }
            organisationId={ currentOrg.id }
          />
        )) }
      </div>
    </div>
  );
}
